import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Save, X, Search, Image } from 'lucide-react';
import { useAlert } from '../../../context/AlertContext';
import { useConfirm } from '../../../context/ConfirmContext';
import Layout from '../../../components/Layout';
import API_BASE_URL, { getAuthHeaders, getAuthToken } from '../../../config/api';

const ProductsManagement = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [editingProduct, setEditingProduct] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [uploading, setUploading] = useState(false);

    const [formData, setFormData] = useState({
        product_name: '',
        category_id: '',
        description: '',
        price: '',
        pv_points: '',
        stock_quantity: '',
        image_url: '',
        buy_quantity: '0',
        get_free_quantity: '0',
        free_product_id: '',
        status: 'active'
    });

    const { showAlert } = useAlert();
    const { showConfirm } = useConfirm();

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const token = getAuthToken();
            // Fetch products and categories in parallel
            const [productsRes, categoriesRes] = await Promise.all([
                fetch(`${API_BASE_URL}/admin/products.php?_cb=${Date.now()}${token ? `&token=${token}` : ''}`, {
                    headers: getAuthHeaders()
                }),
                fetch(`${API_BASE_URL}/admin/product_categories.php?_cb=${Date.now() + 1}${token ? `&token=${token}` : ''}`, {
                    headers: getAuthHeaders()
                })
            ]);

            const productsData = await productsRes.json();
            const categoriesData = await categoriesRes.json();

            if (productsData.success) setProducts(productsData.products);
            if (categoriesData.success) setCategories(categoriesData.categories);

        } catch (error) {
            console.error('Error fetching data:', error);
            showAlert('error', 'Failed to fetch products data');
        } finally {
            setLoading(false);
        }
    }, [showAlert]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                showAlert('error', 'Please select an image file');
                return;
            }
            // Validate file size (5MB)
            if (file.size > 5 * 1024 * 1024) {
                showAlert('error', 'Image size should be less than 5MB');
                return;
            }
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const uploadImage = async () => {
        if (!imageFile) return formData.image_url;

        setUploading(true);
        try {
            const uploadFormData = new FormData();
            uploadFormData.append('image', imageFile);
            uploadFormData.append('type', 'product');

            const token = getAuthToken();
            const response = await fetch(`${API_BASE_URL}/admin/upload_image.php${token ? `?token=${token}` : ''}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: uploadFormData
            });

            const data = await response.json();
            if (data.success) {
                return data.image_url;
            } else {
                throw new Error(data.message || 'Upload failed');
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            showAlert('error', 'Failed to upload image');
            return formData.image_url;
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Upload image first if there's a new file
            const imageUrl = await uploadImage();

            const url = `${API_BASE_URL}/admin/products.php`;
            const method = editingProduct ? 'PUT' : 'POST';
            const body = editingProduct
                ? { ...formData, image_url: imageUrl, id: editingProduct.id }
                : { ...formData, image_url: imageUrl };

            const token = getAuthToken();
            const response = await fetch(`${url}${token ? `?token=${token}` : ''}`, {
                method,
                headers: getAuthHeaders(),
                body: JSON.stringify(body)
            });

            const data = await response.json();

            if (data.success) {
                showAlert('success', `Product ${editingProduct ? 'updated' : 'created'} successfully`);
                fetchData(); // Refresh list
                closeModal();
            } else {
                showAlert('error', data.message || 'Operation failed');
            }
        } catch (error) {
            console.error('Error saving product:', error);
            showAlert('error', 'An error occurred while saving');
        }
    };

    const handleDelete = (product) => {
        const packageCount = parseInt(product.package_count) || 0;
        const message = packageCount > 0
            ? `WARNING: This product is included in ${packageCount} packages. Deleting it will remove it from those packages and ALL active user carts. Are you sure you want to proceed?`
            : `Are you sure you want to delete "${product.product_name}"? This action cannot be undone.`;

        showConfirm(
            'Delete Product',
            message,
            'danger'
        ).then(async (confirmed) => {
            if (confirmed) {
                try {
                    const token = getAuthToken();
                    const response = await fetch(`${API_BASE_URL}/admin/products.php${token ? `?token=${token}` : ''}`, {
                        method: 'DELETE',
                        headers: getAuthHeaders(),
                        body: JSON.stringify({ id: product.id })
                    });
                    const data = await response.json();
                    if (data.success) {
                        showAlert('success', 'Product deleted successfully');
                        fetchData();
                    } else {
                        showAlert('error', data.message || 'Delete failed');
                    }
                } catch (error) {
                    showAlert('error', 'An error occurred while deleting');
                }
            }
        });
    };

    const openModal = (product = null) => {
        if (product) {
            setEditingProduct(product);
            setFormData({
                product_name: product.product_name,
                category_id: product.category_id,
                description: product.description || '',
                price: product.price,
                pv_points: product.pv_points,
                stock_quantity: product.stock_quantity,
                image_url: product.image_url || '',
                buy_quantity: product.buy_quantity || '0',
                get_free_quantity: product.get_free_quantity || '0',
                free_product_id: product.free_product_id || '',
                status: product.status
            });
            setImagePreview(product.image_url || '');
            setImageFile(null);
        } else {
            setEditingProduct(null);
            setFormData({
                product_name: '',
                category_id: categories.length > 0 ? categories[0].id : '',
                description: '',
                price: '',
                pv_points: '0',
                stock_quantity: '0',
                image_url: '',
                buy_quantity: '0',
                get_free_quantity: '0',
                free_product_id: '',
                status: 'active'
            });
            setImagePreview('');
            setImageFile(null);
        }
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingProduct(null);
        setImageFile(null);
        setImagePreview('');
    };

    const filteredProducts = products.filter(prod => {
        const matchesSearch = prod.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (prod.description && prod.description.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesCategory = categoryFilter === 'all' || String(prod.category_id) === String(categoryFilter);

        return matchesSearch && matchesCategory;
    });

    return (
        <Layout>
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Products Management</h1>
                        <p className="text-gray-600">Manage your product inventory</p>
                    </div>
                    <button
                        onClick={() => openModal()}
                        className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Plus size={20} />
                        Add Product
                    </button>
                </div>

                {/* Filters */}
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-6 flex flex-col md:flex-row items-center gap-4">
                    <div className="relative flex-1 w-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search products..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        />
                    </div>

                    <div className="w-full md:w-64">
                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        >
                            <option value="all">All Categories</option>
                            {categories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Products Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-4 font-semibold text-gray-700 w-16">Image</th>
                                    <th className="px-6 py-4 font-semibold text-gray-700">Product Name</th>
                                    <th className="px-6 py-4 font-semibold text-gray-700">Category</th>
                                    <th className="px-6 py-4 font-semibold text-gray-700 text-right">Price</th>
                                    <th className="px-6 py-4 font-semibold text-gray-700 text-right">PV</th>
                                    <th className="px-6 py-4 font-semibold text-gray-700 text-center">Stock</th>
                                    <th className="px-6 py-4 font-semibold text-gray-700">Offers</th>
                                    <th className="px-6 py-4 font-semibold text-gray-700 text-center">Status</th>
                                    <th className="px-6 py-4 font-semibold text-gray-700 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {loading ? (
                                    <tr>
                                        <td colSpan="8" className="px-6 py-8 text-center text-gray-500">Loading...</td>
                                    </tr>
                                ) : filteredProducts.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="px-6 py-8 text-center text-gray-500">No products found</td>
                                    </tr>
                                ) : (
                                    filteredProducts.map((product) => (
                                        <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-3">
                                                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden border border-gray-200">
                                                    {product.image_url ? (
                                                        <img src={product.image_url} alt="" className="w-full h-full object-cover" />
                                                    ) : (
                                                        <Image size={20} className="text-gray-400" />
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-3">
                                                <div className="font-semibold text-gray-900">{product.product_name}</div>
                                                <div className="text-xs text-gray-500 truncate max-w-[200px]">{product.description}</div>
                                            </td>
                                            <td className="px-6 py-3">
                                                <span className="text-gray-600 bg-gray-100 px-2 py-1 rounded text-xs">
                                                    {product.category_name}
                                                </span>
                                            </td>
                                            <td className="px-6 py-3 text-right font-medium">
                                                ₹{parseFloat(product.price).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-3 text-right text-gray-600">
                                                {parseFloat(product.pv_points).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-3 text-center">
                                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${parseInt(product.stock_quantity) > 10
                                                    ? 'bg-green-100 text-green-700'
                                                    : parseInt(product.stock_quantity) > 0
                                                        ? 'bg-orange-100 text-orange-700'
                                                        : 'bg-red-100 text-red-700'
                                                    }`}>
                                                    {product.stock_quantity}
                                                </span>
                                            </td>
                                            <td className="px-6 py-3">
                                                {parseInt(product.buy_quantity) > 0 && parseInt(product.get_free_quantity) > 0 ? (
                                                    <div className="flex flex-col gap-1">
                                                        <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100 inline-block w-fit">
                                                            BUY {product.buy_quantity} GET {product.get_free_quantity} FREE
                                                        </span>
                                                        <span className="text-[9px] text-gray-400 font-medium truncate max-w-[120px]">
                                                            {product.free_product_id ? `(${product.free_product_name})` : '(Same Item)'}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400 text-xs">No Offer</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-3 text-center">
                                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${product.status === 'active'
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'bg-red-100 text-red-700'
                                                    }`}>
                                                    {product.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-3 text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => openModal(product)}
                                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Edit2 size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(product)}
                                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Modal */}
                {showModal && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50 sticky top-0 z-10">
                                <h2 className="text-xl font-bold text-gray-900">
                                    {editingProduct ? 'Edit Product' : 'Add New Product'}
                                </h2>
                                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                                    <X size={24} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="p-6 space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                                        <input
                                            type="text"
                                            name="product_name"
                                            value={formData.product_name}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                        <select
                                            name="category_id"
                                            value={formData.category_id}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                        >
                                            <option value="">Select Category</option>
                                            {categories.map(cat => (
                                                <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                        <select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹)</label>
                                        <input
                                            type="number"
                                            name="price"
                                            value={formData.price}
                                            onChange={handleInputChange}
                                            required
                                            min="0"
                                            step="0.01"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">PV Points</label>
                                        <input
                                            type="number"
                                            name="pv_points"
                                            value={formData.pv_points}
                                            onChange={handleInputChange}
                                            min="0"
                                            step="0.01"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Stock Quantity</label>
                                        <input
                                            type="number"
                                            name="stock_quantity"
                                            value={formData.stock_quantity}
                                            onChange={handleInputChange}
                                            min="0"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                        />
                                    </div>

                                    {/* BOGO Section */}
                                    <div className="col-span-2 border-t border-gray-100 pt-4 mt-2">
                                        <h3 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-4">Promotions (Buy Get Free)</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">Buy Quantity</label>
                                                <input
                                                    type="number"
                                                    name="buy_quantity"
                                                    value={formData.buy_quantity}
                                                    onChange={handleInputChange}
                                                    min="0"
                                                    placeholder="e.g. 2"
                                                    className="w-full px-4 py-2 border border-blue-100 bg-blue-50/30 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-bold"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">Get Free Quantity</label>
                                                <input
                                                    type="number"
                                                    name="get_free_quantity"
                                                    value={formData.get_free_quantity}
                                                    onChange={handleInputChange}
                                                    min="0"
                                                    placeholder="e.g. 1"
                                                    className="w-full px-4 py-2 border border-blue-100 bg-blue-50/30 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-bold"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">Free Item</label>
                                                <select
                                                    name="free_product_id"
                                                    value={formData.free_product_id}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-2 border border-blue-100 bg-blue-50/30 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                                >
                                                    <option value="">(Same Product)</option>
                                                    {products.filter(p => p.id !== editingProduct?.id).map(p => (
                                                        <option key={p.id} value={p.id}>{p.product_name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <p className="text-[10px] text-gray-400 mt-2 italic">
                                            Example: To set "Buy 2 Get 1 Free", Buy Quantity = 2, Get Free = 1. Leave "Free Item" as "(Same Product)" unless giving away a different item.
                                        </p>
                                    </div>

                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Product Image</label>
                                        <div className="flex items-start gap-4">
                                            {/* Image Preview */}
                                            <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                                                {imagePreview ? (
                                                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                                                ) : (
                                                    <Image size={40} className="text-gray-400" />
                                                )}
                                            </div>

                                            {/* Upload Button */}
                                            <div className="flex-1">
                                                <input
                                                    type="file"
                                                    id="product-image"
                                                    accept="image/*"
                                                    onChange={handleImageChange}
                                                    className="hidden"
                                                />
                                                <label
                                                    htmlFor="product-image"
                                                    className="inline-block px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer transition-colors border border-gray-300"
                                                >
                                                    Choose Image
                                                </label>
                                                <p className="text-xs text-gray-500 mt-2">
                                                    Supported: JPG, PNG, GIF, WebP (Max 5MB)
                                                </p>
                                                {imageFile && (
                                                    <p className="text-xs text-green-600 mt-1">
                                                        ✓ {imageFile.name}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            rows="4"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                                        ></textarea>
                                    </div>
                                </div>

                                <div className="pt-4 flex justify-end gap-3 border-t border-gray-100">
                                    <button
                                        type="button"
                                        onClick={closeModal}
                                        disabled={uploading}
                                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={uploading}
                                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm hover:shadow-md transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Save size={18} />
                                        {uploading ? 'Uploading...' : 'Save Product'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default ProductsManagement;


